import type { ComponentProps, ReactNode } from 'react'

import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'
import { cn } from '@/shared/lib/utils'

/** 门户 / 账户 / 列表页统一卡片容器（shadcn Card） */
export function PageCard({ className, ...props }: ComponentProps<typeof Card>) {
  return <Card className={cn('gap-0 overflow-hidden shadow-sm', className)} {...props} />
}

type PageSectionProps = {
  title?: ReactNode
  description?: ReactNode
  action?: ReactNode
  footer?: ReactNode
  className?: string
  contentClassName?: string
  children: ReactNode
}

/** 带标题的内容区块 — 替代 `rounded-2xl border border-[#2a2a3a] bg-[#14141d]` */
export function PageSection({
  title,
  description,
  action,
  footer,
  className,
  contentClassName,
  children,
}: PageSectionProps) {
  const hasHeader = title != null || description != null || action != null

  return (
    <PageCard className={className}>
      {hasHeader ? (
        <CardHeader className={cn(action && 'grid-cols-[1fr_auto]')}>
          {title != null ? <CardTitle className="text-sm font-medium text-foreground">{title}</CardTitle> : null}
          {description != null ? <CardDescription>{description}</CardDescription> : null}
          {action != null ? <CardAction>{action}</CardAction> : null}
        </CardHeader>
      ) : null}
      <CardContent className={cn(hasHeader && 'pt-0', contentClassName)}>{children}</CardContent>
      {footer != null ? <CardFooter className="border-t bg-muted/30">{footer}</CardFooter> : null}
    </PageCard>
  )
}

/** 虚线空状态容器 */
export function PageEmptyCard({
  className,
  children,
}: {
  className?: string
  children: ReactNode
}) {
  return (
    <PageCard
      className={cn(
        'ring-1 ring-border bg-card/80 px-6 py-12 text-center text-sm text-muted-foreground',
        className,
      )}
    >
      {children}
    </PageCard>
  )
}

export { CardContent, CardFooter, CardHeader, CardTitle, CardDescription }
